import { useTranslation } from 'react-i18next'
import type { CardComponentProps } from 'nextstepjs'
import Button from '@/shared/components/Button/Button'
import styles from './OnboardingCard.module.scss'

function OnboardingCard({
  step,
  currentStep,
  totalSteps,
  nextStep,
  prevStep,
  skipTour,
  arrow
}: CardComponentProps) {
  const { t } = useTranslation()

  // The library can briefly call this with step=undefined during skip/close
  // transitions. Bail out before reading any step fields.
  if (!step) return null

  const isFirst = currentStep === 0
  const isLast = currentStep === totalSteps - 1

  const renderIcon = () => {
    if (!step.icon) return null
    if (typeof step.icon === 'string') {
      const isClass = step.icon.startsWith('la')
      if (isClass) {
        return (
          <span className={styles.icon}>
            <i className={step.icon} aria-hidden="true" />
          </span>
        )
      }
      return <span className={styles.icon}>{step.icon}</span>
    }
    return <span className={styles.icon}>{step.icon}</span>
  }

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          {renderIcon()}
          <p className={`title-bold-small ${styles.title}`}>{step.title}</p>
        </div>
        <span className={`body-small ${styles.stepCounter}`}>
          {currentStep + 1} / {totalSteps}
        </span>
      </div>

      <div className={`body-medium ${styles.content}`}>{step.content}</div>

      <div className={styles.progress}>
        {Array.from({ length: totalSteps }).map((_, i) => {
          let cls = styles.dot
          if (i === currentStep) cls = `${styles.dot} ${styles.active}`
          else if (i < currentStep) cls = `${styles.dot} ${styles.past}`
          return <span key={`dot-${i}`} className={cls} />
        })}
      </div>

      <div className={styles.footer}>
        {skipTour ? (
          <Button
            onClick={skipTour}
            label={t('ONBOARDING.ACTIONS.SKIP')}
            variant="text"
            color="primary"
            smallFont
            condensedLabel
          />
        ) : (
          <span />
        )}
        <div className={styles.footerActions}>
          {!isFirst && (
            <Button
              onClick={prevStep}
              label={t('ONBOARDING.ACTIONS.BACK')}
              variant="outlined"
              color="primary"
              smallFont
              condensedLabel
            />
          )}
          <Button
            onClick={nextStep}
            label={
              isLast
                ? t('ONBOARDING.ACTIONS.FINISH')
                : t('ONBOARDING.ACTIONS.NEXT')
            }
            variant="contained"
            color="secondary"
            smallFont
            condensedLabel
          />
        </div>
      </div>

      {arrow}
    </div>
  )
}

export default OnboardingCard
